import { createBuyerIntentCore } from './core.js';
import {
  collectSubjectIds,
  readElementConfig,
  readSessionConfig,
  readTags,
  readViewContext,
  resolveEventName,
} from './dom.js';
import { createIdentityManager } from './identity.js';
import { sendWithBeacon } from './transport.js';

const DEFAULT_ACTIVITY_ENDPOINT = '/activity/events';
const DEFAULT_VIEW_NAME = '$view';
const DEFAULT_CLICK_NAME = '$click';

const firedViewElements = new WeakSet();

export function createBuyerIntentSDK() {
  const core = createBuyerIntentCore();
  const identity = createIdentityManager();
  let activityEndpoint = DEFAULT_ACTIVITY_ENDPOINT;
  let origin = null;
  let viewName = DEFAULT_VIEW_NAME;
  let userType = 'standard';
  let lastPageKey = null;
  let initialLandingPage = null;
  let initialReferrer = null;
  let initialUtmParams = null;

  function readUtmParams(urlValue) {
    if (!urlValue) {
      return {};
    }

    const url = new URL(urlValue, window.location.origin);
    const params = url.searchParams;

    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_term: params.get('utm_term') || undefined,
      utm_content: params.get('utm_content') || undefined,
    };
  }

  function buildVisitContext() {
    if (typeof window === 'undefined') {
      return {};
    }

    const landingPage = initialLandingPage || window.location.href;
    return {
      landingPage,
      referrer: initialReferrer || document.referrer || undefined,
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      utmParams: initialUtmParams || readUtmParams(landingPage),
    };
  }

  function applyElementConfig(element) {
    const config = readElementConfig(element);

    if (config.activityEndpoint) {
      setActivityEndpoint(config.activityEndpoint);
    }

    if (config.origin || config.userType || config.distinctId) {
      setBaseProperties({
        origin: config.origin,
        userType: config.userType,
        distinctId: config.distinctId,
      });
    }
  }

  function applyDomTags(element) {
    const elements = element ? new Set([element]) : undefined;
    const entries = readTags(elements);
    entries.forEach(({ element: tagElement, tag }) => {
      applyElementConfig(tagElement);
      core.tagPage(tag);
    });
    return entries.length > 0;
  }

  function sendPayload(element, defaultEventName) {
    if (typeof window === 'undefined') {
      return false;
    }

    const eventName = resolveEventName(element, defaultEventName);
    const payload = core.buildPayload({
      name: eventName,
      url: window.location.href,
      origin: origin || window.location.hostname,
      distinctId: identity.ensure(),
      userType,
      visit: buildVisitContext(),
    });

    const sent = sendWithBeacon(activityEndpoint, payload);
    core.resetPageState();
    return sent;
  }

  function trackPageviewInternal(element) {
    if (typeof window === 'undefined') {
      return false;
    }

    const pageKey = `${window.location.pathname}${window.location.search}`;
    if (pageKey === lastPageKey) {
      return false;
    }

    lastPageKey = pageKey;
    return sendPayload(element, viewName);
  }

  function trackPageview(element) {
    if (typeof window === 'undefined') {
      return false;
    }

    const pageKey = `${window.location.pathname}${window.location.search}`;
    if (pageKey === lastPageKey) {
      return false;
    }

    lastPageKey = pageKey;
    const hasTags = applyDomTags(element);
    if (!hasTags) {
      return false;
    }

    return sendPayload(element, viewName);
  }

  function trackClick(element) {
    if (typeof window === 'undefined' || !element) {
      return false;
    }

    return sendPayload(element, DEFAULT_CLICK_NAME);
  }

  function init(options = {}) {
    activityEndpoint = options.activityEndpoint || activityEndpoint;
    origin = options.origin || origin;
    viewName = options.viewName || viewName;
    userType = options.userType || userType;

    if (options.distinctId) {
      identity.set(options.distinctId);
    }

    if (typeof window !== 'undefined' && !initialLandingPage) {
      initialLandingPage = window.location.href;
      initialReferrer = document.referrer || undefined;
      initialUtmParams = readUtmParams(initialLandingPage);
    }

    core.setBaseProperties({
      origin: origin || undefined,
      distinctId: identity.get() || undefined,
      userType,
    });
  }

  function setActivityEndpoint(value) {
    activityEndpoint = value || activityEndpoint;
  }

  function setBaseProperties(options = {}) {
    if (options.origin) {
      origin = options.origin;
    }

    if (options.distinctId) {
      identity.set(options.distinctId);
    }

    if (options.userType) {
      userType = options.userType;
    }

    core.setBaseProperties({
      origin: origin || undefined,
      distinctId: identity.get() || undefined,
      userType,
    });
  }

  function tagPage(options = {}) {
    core.tagPage(options);
  }

  function setVisitProperties(options = {}) {
    core.setVisitProperties(options);
  }

  function trackNextRouter(nextRouter) {
    if (!nextRouter || !nextRouter.events) {
      return () => {};
    }

    const handler = () => trackPageview();
    nextRouter.events.on('routeChangeComplete', handler);
    return () => nextRouter.events.off('routeChangeComplete', handler);
  }

  function resolveConfig(element) {
    const session = readSessionConfig(element);
    const own = readElementConfig(element);
    return {
      origin: session.origin || own.origin,
      activityEndpoint: session.activityEndpoint || own.activityEndpoint,
      userType: session.userType || own.userType,
      distinctId: session.distinctId || own.distinctId,
    };
  }

  function applyConfig(config) {
    if (config.activityEndpoint) {
      setActivityEndpoint(config.activityEndpoint);
    }
    if (config.origin || config.userType || config.distinctId) {
      setBaseProperties({
        origin: config.origin,
        userType: config.userType,
        distinctId: config.distinctId,
      });
    }
  }

  function handleView(element) {
    if (!element || firedViewElements.has(element)) {
      return false;
    }

    firedViewElements.add(element);

    // 1. Resolve config: session ancestor > element's own data-* attrs
    const config = resolveConfig(element);
    applyConfig(config);

    // 2. Read page tags from element's data-buyer-intent (existing flow)
    const entries = readTags(new Set([element]));
    entries.forEach(({ element: tagElement, tag }) => {
      if (tagElement !== element) {
        applyElementConfig(tagElement);
      }
      core.tagPage(tag);
    });

    // 3. Collect IDs from descendant <buyer-intent-subject> elements
    const subjectIds = collectSubjectIds(element);
    if (subjectIds.productIds.length || subjectIds.categoryIds.length) {
      core.tagPage(subjectIds);
    }

    // 4. Fire pageview
    return trackPageviewInternal(element, config);
  }

  function handleClick(element) {
    if (!element) {
      return false;
    }

    // 1. Resolve config: session ancestor > element's own data-* attrs
    const config = resolveConfig(element);
    applyConfig(config);

    // 2. Inherit tag/IDs from ancestor <buyer-intent-view>
    const viewCtx = readViewContext(element);
    if (viewCtx && typeof viewCtx === 'object') {
      core.tagPage(viewCtx);
    }

    // 3. Also read click element's own data-buyer-intent
    const entries = readTags(new Set([element]));
    entries.forEach(({ tag }) => core.tagPage(tag));

    return trackClick(element);
  }

  function listenForElements() {
    if (typeof document === 'undefined') {
      return;
    }

    if (document.__buyerIntentListenersBound) {
      return;
    }

    document.__buyerIntentListenersBound = true;

    document.addEventListener('buyerintent:view', (e) => {
      handleView(e.target);
    });

    document.addEventListener('buyerintent:click', (e) => {
      handleClick(e.target);
    });
  }

  listenForElements();

  return {
    init,
    trackPageview,
    trackClick,
    tagPage,
    setBaseProperties,
    setVisitProperties,
    setActivityEndpoint,
    trackNextRouter,
    listenForElements,
  };
}

let _instance;

export function ensureSDK() {
  if (!_instance) {
    _instance = createBuyerIntentSDK();
  }
  _instance.listenForElements();
  return _instance;
}

export const buyerIntent = ensureSDK();
